import React, { useState, useEffect, useRef } from 'react';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Badge from '../ui/Badge';
import { sloStatusTone } from '../ui/badgeTones';

/**
 * formatLatency — adaptive seconds/minutes/hours formatter.
 *  - < 60 s     → "Ns"          e.g. "32s"
 *  - 60-3599 s  → "M.M min"     e.g. "8.2 min"
 *  - 3600-86399 → "Hh Mmin"     e.g. "2h 30min"
 *  - >= 86400 s → "Dd Hh"       e.g. "3d 4h"
 * Always uses "min" rather than "m" to disambiguate from millions/meters.
 * Returns '—' for null/undefined/NaN.
 */
export function formatLatency(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Number(seconds);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${(s / 60).toFixed(1)} min`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return `${h}h ${m}min`;
  }
  const d = Math.floor(s / 86400);
  const h = Math.round((s % 86400) / 3600);
  return `${d}d ${h}h`;
}

export function formatLatencyMinutes(seconds, decimals = 2) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  return (Number(seconds) / 60).toFixed(decimals);
}

/**
 * Hand-coded SVG primitives for the SLO dashboard.
 * No charting library — keeps the bundle thin and matches the SummaryView Tailwind style.
 */

// ---------------------------------------------------------------------------
// MiniLineChart — daily series + threshold reference line.
// Props:
//   series:   [{ day: 'YYYY-MM-DD', value: number }]   already sorted ascending by day
//   threshold: number                                  horizontal reference line
//   yLabel:   'minutes' | 'seconds'                    axis label
//   width / height: SVG dimensions (default 480 x 80)
// ---------------------------------------------------------------------------
export function MiniLineChart({
  series = [],
  threshold = null,
  yLabel = '',
  width = 480,
  height = 80,
}) {
  if (!Array.isArray(series) || series.length < 2) {
    return (
      <div className="text-xs text-gray-500 italic">
        {series.length === 0
          ? 'No daily data yet.'
          : 'Awaiting more days of data (need ≥7 for trend).'}
      </div>
    );
  }

  const PAD_L = 28;
  const PAD_R = 8;
  const PAD_T = 8;
  const PAD_B = 16;
  const innerW = width - PAD_L - PAD_R;
  const innerH = height - PAD_T - PAD_B;

  const values = series.map((s) => Number(s.value) || 0);
  const dataMax = Math.max(...values, threshold ?? 0);
  const yMax = dataMax > 0 ? dataMax * 1.15 : 1; // 15% headroom

  const xStep = innerW / Math.max(series.length - 1, 1);
  const yScale = (v) => PAD_T + innerH - (v / yMax) * innerH;

  const points = series.map((s, i) => `${PAD_L + i * xStep},${yScale(values[i])}`);
  const polyline = points.join(' ');

  const thresholdY = threshold != null ? yScale(threshold) : null;

  // X-axis labels: show first, middle, last
  const labelIdx = [0, Math.floor(series.length / 2), series.length - 1];

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Y axis */}
      <line
        x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={height - PAD_B}
        stroke="#374151" strokeWidth="1"
      />
      {/* X axis */}
      <line
        x1={PAD_L} y1={height - PAD_B} x2={width - PAD_R} y2={height - PAD_B}
        stroke="#374151" strokeWidth="1"
      />
      {/* Threshold reference line */}
      {thresholdY != null && (
        <>
          <line
            x1={PAD_L} y1={thresholdY} x2={width - PAD_R} y2={thresholdY}
            stroke="#dc2626"
            strokeWidth="1"
            strokeDasharray="4 3"
            opacity="0.7"
          />
          <text
            x={width - PAD_R - 2}
            y={thresholdY - 3}
            fontSize="9"
            fill="#fca5a5"
            textAnchor="end"
          >
            {threshold} {yLabel}
          </text>
        </>
      )}
      {/* Data line */}
      <polyline
        points={polyline}
        fill="none"
        stroke="#60a5fa"
        strokeWidth="1.6"
      />
      {/* Data points */}
      {series.map((s, i) => (
        <circle
          key={s.day || i}
          cx={PAD_L + i * xStep}
          cy={yScale(values[i])}
          r="2"
          fill="#60a5fa"
        >
          <title>{`${s.day}: ${values[i]} ${yLabel}`}</title>
        </circle>
      ))}
      {/* Y-axis labels (max + 0) */}
      <text x={PAD_L - 3} y={PAD_T + 3} fontSize="9" fill="#6b7280" textAnchor="end">
        {Math.round(yMax)}
      </text>
      <text x={PAD_L - 3} y={height - PAD_B + 3} fontSize="9" fill="#6b7280" textAnchor="end">
        0
      </text>
      {/* X-axis labels (first / mid / last day) */}
      {labelIdx.map((idx) => {
        const day = series[idx]?.day || '';
        const label = day.slice(5); // MM-DD
        return (
          <text
            key={`xl-${idx}`}
            x={PAD_L + idx * xStep}
            y={height - 3}
            fontSize="9"
            fill="#6b7280"
            textAnchor={idx === 0 ? 'start' : idx === series.length - 1 ? 'end' : 'middle'}
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// statusLabel — human label for an SLO status.
// ---------------------------------------------------------------------------
export function statusLabel(status) {
  switch (String(status || '').toLowerCase()) {
    case 'no-data':   return 'NO DATA';
    case 'scheduled': return 'DAILY (NO RECENT)';
    case 'ok':        return 'OK';
    case 'warn':      return 'WARN';
    case 'breach':    return 'EXCEEDED';
    default:          return (status || '—').toUpperCase();
  }
}

// ---------------------------------------------------------------------------
// StatusGrid — current-status summary (workload x metrics).
// Props:
//   rows: [{ workload, samples, p95Min/Sec, p99Min/Sec, status, ...metrics }]
//   columns: [{ key, label, fmt?: (v, row) => string, align?: 'right' }]
//   onRowClick(row): optional
// ---------------------------------------------------------------------------
export function StatusGrid({ rows = [], columns = [], onRowClick = null, emptyText = 'No data.' }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">{emptyText}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-gray-800">
            <th className="px-3 py-2 font-medium text-gray-400">Workload</th>
            <th className="px-3 py-2 font-medium text-gray-400">Status</th>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`px-3 py-2 font-medium text-gray-400 ${c.align === 'right' ? 'text-right' : ''}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isCadenceRest = row.status === 'scheduled';
            return (
              <tr
                key={row.workload}
                className={`border-b border-gray-800/50 hover:bg-gray-900/40 ${onRowClick ? 'cursor-pointer' : ''} ${isCadenceRest ? 'opacity-70' : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                <td className="px-3 py-2 font-mono text-xs text-gray-300">{row.workload}</td>
                <td className="px-3 py-2">
                  <Badge tone={sloStatusTone(row.status)} dot>
                    {statusLabel(row.status)}
                  </Badge>
                </td>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3 py-2 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}
                  >
                    {isCadenceRest && row[c.key] == null
                      ? <span className="text-gray-600 italic">—</span>
                      : (c.fmt ? c.fmt(row[c.key], row) : (row[c.key] ?? '—'))}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeatmapGrid — workload x tenant cells, color by status.
// Props:
//   cells: [{ workload, tenantId, p99Min/Sec, status }]
//   workloads: string[]   row order
//   tenants: string[]     column order
//   valueKey: 'p99Min' | 'p95Sec' | 'p99Sec'
//   valueLabel: 'min' | 's'
// ---------------------------------------------------------------------------
export function HeatmapGrid({
  cells = [],
  workloads = [],
  tenants = [],
  valueKey = 'p99Min',
  valueLabel = 'min',
  tenantLookup = {},
}) {
  if (!cells || cells.length === 0) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">No tenant-level data in the last hour.</div>;
  }

  const cellMap = new Map();
  for (const c of cells) {
    cellMap.set(`${c.workload}|${c.tenantId}`, c);
  }

  const statusBg = (status) => {
    switch (status) {
      case 'breach': return 'bg-red-900/60 text-red-200 border-red-700/60';
      case 'warn':   return 'bg-amber-900/40 text-amber-200 border-amber-700/50';
      case 'ok':     return 'bg-emerald-900/30 text-emerald-200 border-emerald-700/40';
      default:       return 'bg-gray-900/40 text-gray-500 border-gray-800';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-separate" style={{ borderSpacing: '2px' }}>
        <thead>
          <tr>
            <th className="px-2 py-1 font-medium text-gray-500 sticky left-0 bg-gray-950 text-left">Workload</th>
            {tenants.map((t) => (
              <th
                key={t}
                className="px-2 py-1 font-mono text-[10px] text-gray-500 text-center"
                title={tenantLookup[t] || t}
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', minWidth: '24px' }}
              >
                {(tenantLookup[t] || t).slice(0, 14)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {workloads.map((wl) => (
            <tr key={wl}>
              <td className="px-2 py-1 font-mono text-[11px] text-gray-300 sticky left-0 bg-gray-950 whitespace-nowrap">
                {wl}
              </td>
              {tenants.map((t) => {
                const cell = cellMap.get(`${wl}|${t}`);
                if (!cell) {
                  return (
                    <td key={t} className="px-1.5 py-1 border border-gray-900/60 bg-gray-900/20 text-gray-700 text-center">·</td>
                  );
                }
                const v = cell[valueKey];
                const display = v == null ? '—' : Number(v).toFixed(1);
                return (
                  <td
                    key={t}
                    className={`px-1.5 py-1 border text-center tabular-nums ${statusBg(cell.status)}`}
                    title={`${wl} · ${tenantLookup[t] || t}: ${display} ${valueLabel} (${cell.samples ?? 0} samples)`}
                  >
                    {display}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectionTable — workload x days-until-breach + slope + R² + projection.
// Props:
//   workloads: [{ workload, slope, rsquare, currentValue/currentP95Sec/currentP99Min,
//                 projectedIn14d, daysUntilBreach, samples }]
//   targetValue: number
//   targetUnit:  'min' | 'sec'
// ---------------------------------------------------------------------------
export function ProjectionTable({
  workloads = [],
  targetValue,
  targetUnit = 'min',
  currentField = 'currentValue',
  projectedField = 'projectedIn14d',
  valueFormatter = null,        // (numericSeconds) => string. Default: 1-decimal in targetUnit.
  slopeFormatter = null,        // (numericSeconds) => string. Default: 3-decimal in targetUnit.
}) {
  if (!workloads || workloads.length === 0) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">No trend data yet.</div>;
  }

  const defaultValueFmt = (v) => {
    if (v == null) return '—';
    return Number(v).toFixed(1);
  };
  const defaultSlopeFmt = (v) => {
    if (v == null) return '—';
    return Number(v).toFixed(3);
  };
  const fmtVal = valueFormatter || defaultValueFmt;
  const fmtSlope = slopeFormatter || defaultSlopeFmt;

  const fmtDays = (d) => {
    if (d == null) return '—';
    if (d < 0) return 'stable';
    if (d === 0) return 'NOW';
    if (d < 1) return '<1 d';
    if (d > 365) return '>1 yr';
    return `${Math.round(d)} d`;
  };

  const dayPill = (d, rsquare) => {
    const lowConf = rsquare != null && rsquare < 0.2;
    let tone = 'neutral';
    if (d === 0) tone = 'critical';
    else if (d > 0 && d <= 14) tone = 'warning';
    else if (d > 14) tone = 'success';
    else tone = 'success';
    return (
      <span className={lowConf ? 'opacity-50' : ''}>
        <Badge tone={tone}>{fmtDays(d)}</Badge>
        {lowConf && <span className="ml-2 text-[10px] text-gray-500" title="R² < 0.2 — low-confidence trend">low conf.</span>}
      </span>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-gray-800">
            <th className="px-3 py-2 font-medium text-gray-400">Workload</th>
            <th className="px-3 py-2 font-medium text-gray-400">Days to exceed</th>
            <th className="px-3 py-2 font-medium text-gray-400 text-right">Current</th>
            <th className="px-3 py-2 font-medium text-gray-400 text-right">Slope / day</th>
            <th className="px-3 py-2 font-medium text-gray-400 text-right">Projected (+14d)</th>
            <th className="px-3 py-2 font-medium text-gray-400 text-right">R²</th>
            <th className="px-3 py-2 font-medium text-gray-400 text-right">Days observed</th>
          </tr>
        </thead>
        <tbody>
          {workloads.map((row) => {
            const cur = row[currentField];
            const proj = row[projectedField];
            return (
              <tr key={row.workload} className="border-b border-gray-800/50 hover:bg-gray-900/40">
                <td className="px-3 py-2 font-mono text-xs text-gray-300">{row.workload}</td>
                <td className="px-3 py-2">{dayPill(row.daysUntilBreach, row.rsquare)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtVal(cur)}
                  <span className="ml-1 text-[10px] text-gray-500">{targetUnit}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-400">
                  {fmtSlope(row.slope)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtVal(proj)}
                  <span className="ml-1 text-[10px] text-gray-500">{targetUnit}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                  {row.rsquare == null ? '—' : Number(row.rsquare).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{row.samples ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-gray-600 mt-2 px-3">
        Target: {fmtVal(targetValue)} {targetUnit}. Linear fit over up to {Math.max(...workloads.map((w) => w.samples || 0))} daily aggregates.
        Rows with R² &lt; 0.2 are dimmed (trend is noisy / not a clean line).
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section — small reusable section header w/ optional right slot.
// ---------------------------------------------------------------------------
export function Section({ title, subtitle, right, children }) {
  return (
    <section className="card mb-6">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-gray-200">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {right && <div className="flex-shrink-0">{right}</div>}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// MetricsLegend — small (i) icon button that opens a popover explaining
// p50/p95/p99/avg etc. Place in a Section's `right` slot. Subtle and easy
// to find but takes minimal space when collapsed.
// ---------------------------------------------------------------------------
const DEFAULT_LEGEND_ITEMS = [
  { term: 'avg', defn: 'Mean. Sum of values divided by count. Skewed by outliers.' },
  { term: 'p50 (median)', defn: 'Half of all samples are at or below this value. The "typical" experience.' },
  { term: 'p95', defn: '95% of samples are at or below this value. Catches almost all real-world latency, ignores extreme outliers.' },
  { term: 'p99', defn: '99% of samples are at or below this value. The "tail" — what your worst 1% of records experience. Used for SLO alerting.' },
  { term: 'max', defn: 'The single slowest record in the window. Useful for hunting outliers; not a stable signal.' },
];

export function MetricsLegend({ items = DEFAULT_LEGEND_ITEMS, label = 'What do these terms mean?' }) {
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div className="relative inline-block" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-gray-500 hover:text-gray-300 transition-colors p-1 -m-1 rounded"
        aria-label={label}
        title={label}
      >
        <InformationCircleIcon className="h-5 w-5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-700 bg-gray-900 shadow-xl p-4 text-xs">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-200">Metric definitions</h3>
            <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-300 -mr-1 -mt-1 p-1">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          <dl className="space-y-2 leading-snug">
            {items.map((it) => (
              <div key={it.term}>
                <dt className="font-mono text-blue-300">{it.term}</dt>
                <dd className="text-gray-400 mt-0.5">{it.defn}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TenantIndicator — chip showing which tenant the data was actually fetched
// for (vs the dropdown selection). When the dropdown changes but a fetch
// hasn't completed, shows "Updating to X…" so the user knows the data on
// screen is still for the previous tenant.
//
// Props:
//   selectedTenantId : current dropdown value (string, '' for all)
//   displayedTenantId: tenant the on-screen data was fetched for (string, '' for all)
//   tenantLookup     : { [id]: friendlyName }
//   loading          : boolean — fetch is in flight
// ---------------------------------------------------------------------------
export function TenantIndicator({ selectedTenantId = '', displayedTenantId = null, tenantLookup = {}, loading = false }) {
  const labelFor = (id) => {
    if (!id) return 'All tenants';
    return tenantLookup[id] || id;
  };
  const showing = displayedTenantId == null ? 'Loading…' : labelFor(displayedTenantId);
  const isStale = displayedTenantId !== null && selectedTenantId !== displayedTenantId;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-gray-500">Showing data for:</span>
      <span className={`font-medium px-2 py-0.5 rounded ${isStale ? 'bg-amber-900/40 text-amber-200 border border-amber-700/50' : 'bg-gray-800 text-gray-200 border border-gray-700'}`}>
        {showing}
      </span>
      {isStale && (
        <span className="text-amber-400">
          {loading ? `→ updating to ${labelFor(selectedTenantId)}…` : `(refresh pending: ${labelFor(selectedTenantId)})`}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StackedBar — horizontal stacked bar with two segments (e.g. Microsoft+cadence
// vs ours). Shows the total under the bar.
//
// Props:
//   leftSec, rightSec : seconds for each segment
//   leftLabel, rightLabel : tooltip / legend text
//   maxSec           : optional shared scale (so multiple bars are comparable)
// ---------------------------------------------------------------------------
export function StackedBar({
  leftSec = 0,
  rightSec = 0,
  leftLabel = 'Microsoft + cadence',
  rightLabel = 'Our processing',
  maxSec = null,
}) {
  const total = (Number(leftSec) || 0) + (Number(rightSec) || 0);
  const scale = maxSec || total;
  if (!scale) return <span className="text-xs text-gray-600 italic">no data</span>;
  const leftPct = (Number(leftSec) / scale) * 100;
  const rightPct = (Number(rightSec) / scale) * 100;
  return (
    <div className="w-full">
      <div className="w-full bg-gray-900 rounded h-3 overflow-hidden flex" title={`${leftLabel}: ${formatLatency(leftSec)} • ${rightLabel}: ${formatLatency(rightSec)} • Total: ${formatLatency(total)}`}>
        <div className="bg-gray-500 h-full" style={{ width: `${leftPct}%` }} />
        <div className="bg-blue-500 h-full" style={{ width: `${rightPct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px] text-gray-500">
        <span>
          <span className="inline-block w-2 h-2 bg-gray-500 rounded-sm mr-1 align-middle" />
          {leftLabel}: <span className="text-gray-300 tabular-nums">{formatLatency(leftSec)}</span>
        </span>
        <span>
          <span className="inline-block w-2 h-2 bg-blue-500 rounded-sm mr-1 align-middle" />
          {rightLabel}: <span className="text-gray-300 tabular-nums">{formatLatency(rightSec)}</span>
        </span>
        <span className="text-gray-400">total <span className="text-gray-200 tabular-nums">{formatLatency(total)}</span></span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// formatRelativeMinutes — "5m ago", "1h 12m ago" etc.
// ---------------------------------------------------------------------------
export function formatRelativeMinutes(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const ms = Date.now() - t;
    if (ms < 0) return 'just now';
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    const rem = minutes % 60;
    if (hours < 24) return rem ? `${hours}h ${rem}m ago` : `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return '—';
  }
}
